import { z } from 'zod';

/**
 * Asset / meter / tag register contract (CLAUDE.md §10 `GET /api/assets?site` + CRUD,
 * §11.1 `/assets`). Admin-only mutations (`assets:write`); reads are `assets:read`.
 *
 * Single source of truth for the register payload + mutation inputs — imported by the Worker
 * routes, their tests, and the React `/assets` screen. The register is the topology tree
 * site → areas → assets → (meters, tags). Areas mirror the ingest topology and are read-only
 * here; assets/meters/tags are the CRUD surface (protocol config + tag dictionary, §8.2).
 *
 * IDs are generated server-side on create (callers never supply them). Every mutation writes
 * an immutable audit row (§12.3). Deletes are blocked when a row is still referenced (e.g. a
 * tag with readings, or an asset with child meters/tags) — the route returns 409.
 */

// ── Enums (mirror db/schema.ts $type unions) ────────────────────────────────
export const AreaKindSchema = z.enum(['shaft', 'plant', 'surface', 'solar']);
export type AreaKind = z.infer<typeof AreaKindSchema>;

export const AssetTypeSchema = z.enum(['meter', 'plc', 'gateway', 'seu']);
export type AssetType = z.infer<typeof AssetTypeSchema>;

export const ProtocolSchema = z.enum(['opcua', 'modbus', 'dlms', 'mqtt', 'rest', 'file']);
export type Protocol = z.infer<typeof ProtocolSchema>;

export const MeterRoleSchema = z.enum([
  'incomer',
  'submeter',
  'solar',
  'diesel',
  'acetylene',
  'chemical',
  'tank_level',
]);
export type MeterRole = z.infer<typeof MeterRoleSchema>;

export const TagDatatypeSchema = z.enum(['float', 'int', 'bool', 'string']);
export type TagDatatype = z.infer<typeof TagDatatypeSchema>;

// ── Register rows (read) ─────────────────────────────────────────────────────
export const TagRowSchema = z.object({
  id: z.string(),
  assetId: z.string(),
  name: z.string(),
  engUnit: z.string(),
  datatype: TagDatatypeSchema,
  scale: z.number().nullable(),
  deadband: z.number().nullable(),
  /** Model-output tag (Atheon AI); never mixed with metered values (§13). */
  derived: z.boolean(),
  /** Human-captured via the PWA (acetylene/chemical/tank dips); the capture screen lists these. */
  manual: z.boolean(),
});
export type TagRow = z.infer<typeof TagRowSchema>;

export const MeterRowSchema = z.object({
  id: z.string(),
  assetId: z.string(),
  role: MeterRoleSchema,
  accuracyClass: z.string().nullable(),
  unit: z.string(),
});
export type MeterRow = z.infer<typeof MeterRowSchema>;

export const AssetRowSchema = z.object({
  id: z.string(),
  areaId: z.string(),
  type: AssetTypeSchema,
  make: z.string().nullable(),
  model: z.string().nullable(),
  tag: z.string().nullable(),
  protocol: ProtocolSchema.nullable(),
  meta: z.record(z.unknown()).nullable(),
  meters: z.array(MeterRowSchema),
  tags: z.array(TagRowSchema),
});
export type AssetRow = z.infer<typeof AssetRowSchema>;

export const AreaRowSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  name: z.string(),
  kind: AreaKindSchema,
  assets: z.array(AssetRowSchema),
});
export type AreaRow = z.infer<typeof AreaRowSchema>;

/** Full register tree for a site. */
export const AssetRegisterSchema = z.object({
  siteId: z.string(),
  areas: z.array(AreaRowSchema),
});
export type AssetRegister = z.infer<typeof AssetRegisterSchema>;

// ── Mutation inputs (create / update) ────────────────────────────────────────
// Create inputs omit `id` (server-generated). Update inputs are partial patches.

export const AssetCreateSchema = z.object({
  areaId: z.string().min(1),
  type: AssetTypeSchema,
  make: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  tag: z.string().nullable().optional(),
  protocol: ProtocolSchema.nullable().optional(),
  meta: z.record(z.unknown()).nullable().optional(),
});
export type AssetCreate = z.infer<typeof AssetCreateSchema>;

export const AssetUpdateSchema = AssetCreateSchema.partial().refine(
  (o) => Object.keys(o).length > 0,
  { message: 'at least one field required' },
);
export type AssetUpdate = z.infer<typeof AssetUpdateSchema>;

export const MeterCreateSchema = z.object({
  assetId: z.string().min(1),
  role: MeterRoleSchema,
  accuracyClass: z.string().nullable().optional(),
  unit: z.string().min(1),
});
export type MeterCreate = z.infer<typeof MeterCreateSchema>;

export const MeterUpdateSchema = MeterCreateSchema.partial().refine(
  (o) => Object.keys(o).length > 0,
  { message: 'at least one field required' },
);
export type MeterUpdate = z.infer<typeof MeterUpdateSchema>;

export const TagCreateSchema = z.object({
  assetId: z.string().min(1),
  name: z.string().min(1),
  engUnit: z.string().min(1),
  datatype: TagDatatypeSchema,
  scale: z.number().nullable().optional(),
  deadband: z.number().nullable().optional(),
  derived: z.boolean().optional(),
  manual: z.boolean().optional(),
});
export type TagCreate = z.infer<typeof TagCreateSchema>;

export const TagUpdateSchema = TagCreateSchema.partial().refine(
  (o) => Object.keys(o).length > 0,
  { message: 'at least one field required' },
);
export type TagUpdate = z.infer<typeof TagUpdateSchema>;

/** Single-row responses for create/update (the affected row, post-write). */
export const AssetResponseSchema = z.object({ asset: AssetRowSchema });
export type AssetResponse = z.infer<typeof AssetResponseSchema>;
export const MeterResponseSchema = z.object({ meter: MeterRowSchema });
export type MeterResponse = z.infer<typeof MeterResponseSchema>;
export const TagResponseSchema = z.object({ tag: TagRowSchema });
export type TagResponse = z.infer<typeof TagResponseSchema>;
